import { type Theme, alpha } from '@mui/material/styles';

export default function Input(theme: Theme) {
  return {
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            '& svg': {
              color: theme.palette.text.disabled,
            },
          },
        },
        input: {
          '&::placeholder': {
            opacity: 1,
            color: theme.palette.text.disabled,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: alpha(theme.palette.slate[400], 0.56),
          },
          '&:after': {
            borderBottomColor: theme.palette.slate.main,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.text.primary,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          backgroundColor: alpha(theme.palette.slate[500], 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.slate[500], 0.16),
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.slate[500], 0.16),
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          },
          // Commenting out to see if not needed. It's breaking fees amount euro symbol. But don't need if it's needed for somewhere else.
          // '& .MuiInputAdornment-root.MuiInputAdornment-positionStart.MuiInputAdornment-filled':
          //   {
          //     marginTop: '0 !important',
          //   },
        },
        underline: {
          '&:before, :after': {
            display: 'none',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.slate[400], 0.32),
          },
          '&.Mui-focused, &:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: theme.palette.slate.main,
            },
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
        },
      },
    },
    // NOTE: https://mui.com/x/migration/migration-pickers-v7/#new-dom-structure-for-the-field
    // Pickers components now require separate style overrides (e.g. MuiPickersOutlinedInput),
    // as they no longer inherit base input styles — this was introduced as part of the MUI upgrade.
    MuiPickersFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          backgroundColor: alpha(theme.palette.slate[500], 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.slate[500], 0.16),
          },
          '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.slate[500], 0.16),
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          },
          // NOTE: underline overrides don't apply to pickers – use the specific class selector instead
          // https://github.com/mui/mui-x/issues/17578
          '&.MuiPickersFilledInput-underline': {
            '&:before, :after': {
              display: 'none',
            },
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: alpha(theme.palette.slate[400], 0.32),
          },
          // NOTE: Custom focus/hover styles worked fine on TextField, but were ignored on pickers
          // due to MUI using `.Mui-focused:not(.Mui-error)`. Updated selector to match.
          '&.Mui-focused:not(.Mui-error), &:hover:not(.Mui-error)': {
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderWidth: 1,
              borderColor: theme.palette.slate.main,
            },
          },
          '&.Mui-disabled': {
            '& .MuiPickersOutlinedInput-notchedOutline': {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
        },
      },
    },
  };
}
